import { KTSVG } from '../../../_metronic/helpers'
import { FC, useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { Modal } from 'react-bootstrap'

import { useAuth } from '../../../app/modules/auth'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import relativeTime from 'dayjs/plugin/relativeTime'
import calendar from 'dayjs/plugin/calendar'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import CountUp from 'react-countup'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)
dayjs.extend(calendar)
dayjs.extend(localizedFormat)

type Props = {
  show: boolean
  handleClose: () => void
  selectedDate?: string
  selectedRowData?: any
  ref?: any
}

const CURRENCY_APP = process.env.REACT_APP_CURRENCY
const OPENING_TIME = process.env.REACT_APP_OPENING_TIME

export const Dailysummarymodal: FC<Props> = forwardRef<any, Props>((props, ref) => {
  const [selectedDate, setSelectedDate] = useState(props.selectedDate || dayjs().format('YYYY-MM-DD'))
  const { currentUser } = useAuth()

  // Safe utility functions
  const safeNumber = (value: any): number => {
    const num = parseFloat(value)
    return isNaN(num) ? 0 : num
  }

  // New helper function for proper null handling
  const safeNumberOrNull = (value: any): number | null => {
    if (value === null || value === undefined || value === '') {
      return null
    }
    const num = parseFloat(value)
    return isNaN(num) ? null : num
  }

  const formatCurrency = (value: any): string => {
    const num = safeNumber(value)
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  useImperativeHandle(ref, () => ({
    resetModalValues() {
      setSelectedDate(dayjs().format('YYYY-MM-DD'))
    },
  }))

  // Update selected date when props change
  useEffect(() => {
    if (props.selectedDate) {
      setSelectedDate(props.selectedDate)
    }
  }, [props.selectedDate])

  // Calculate date range for expenses
  const startDate = new Date(
    dayjs.unix(dayjs(selectedDate + ' ' + OPENING_TIME).unix()).format('YYYY-MM-DD HH:mm')
  )

  const endDate = new Date(
    dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD') + ' ' + OPENING_TIME
  )

  // Get data from row
  const rowData = props.selectedRowData
  const grossSales = rowData ? safeNumber(rowData.gross_sales) : 0
  const grossDeductions = rowData ? safeNumber(rowData.gross_deductions) : 0
  const netSales = rowData ? safeNumber(rowData.net_sales) : 0
  const net_profit_loss = rowData ? safeNumber(rowData.net_profit_loss) : 0
  const salesEntries = rowData ? safeNumber(rowData.occurrences) : 0
  const hoursOfSelling = rowData ? safeNumber(rowData.hours_selling) : 0
  const costOfGoods = rowData ? safeNumber(rowData.cogs) : 0

  // Updated calculations with proper null handling
  const openingBalance = safeNumberOrNull(rowData?.total_opening_float) ?? 0
  const closingBalance = safeNumberOrNull(rowData?.total_closing_float) // Keep as null if not set
  const totalExpenses = safeNumberOrNull(rowData?.total_expenses) ?? 0

  // Get payment totals dynamically
  const paymentTotals = rowData?.payment_totals || []

  // Find cash payments specifically for variance calculation
  const cashPaymentData = paymentTotals.find(
    (p: any) => p.payment_type_name?.toUpperCase() === 'CASH'
  )
  const cashPayments = safeNumberOrNull(cashPaymentData?.total_amount) ?? 0

  // Debt payments collected are physical cash received — include in expected cash
  const debtPaymentData = paymentTotals.find(
    (p: any) => p.payment_type_name?.toUpperCase() === 'DEBT PAYMENT'
  )
  const debtPaymentsCollected = safeNumberOrNull(debtPaymentData?.total_amount) ?? 0

  // Calculate expected cash and variance only if we have closing balance
  const expectedCashAtEOD = openingBalance + cashPayments + debtPaymentsCollected - totalExpenses
  const variance = closingBalance !== null ? closingBalance - expectedCashAtEOD : null

  const formatDateRange = () => {
    if (rowData) {
      const start = dayjs(rowData.date_start)
      const end = dayjs(rowData.date_end)
      return `${start.format('MMMM DD, YYYY')} (${start.format('LT')} - ${end.format('LT')})`
    } else {
      const start = dayjs(startDate)
      const end = dayjs(endDate)
      return `${start.format('MMMM DD, YYYY')} (${start.format('LT')} - ${end.format('LT')})`
    }
  }

  const formatPaymentTypeName = (name: string) => {
    if (!name) return 'Unknown Sales'
    if (name.toUpperCase() === 'DEBT PAYMENT') return 'Debt Payment'
    return `${name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()} Sales`
  }

  return (
    <Modal
      className='modal fade'
      id='kt_modal_daily_summary'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={props.show}
      centered
      dialogClassName='modal-lg'
      aria-hidden='true'
      onHide={props.handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='fw-bolder'>
            Daily Summary
            <span className='h-10px border-gray-200 border-start mx-2'></span>
            <span className='text-muted fw-bold fs-6'>{formatDateRange()}</span>
          </h3>

          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            onClick={props.handleClose}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>

        <div className='modal-body'>
          {rowData && (
            <div className='card'>
              <div className='card-body py-3'>
                {/* Sales Activity */}
                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Sales Entries:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp end={salesEntries} duration={1} separator=',' />
                    </span>
                  </div>
                </div>

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Hours Selling:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp end={hoursOfSelling} duration={1} decimals={1} separator=',' /> hrs
                    </span>
                  </div>
                </div>

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Average per Hour:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp
                        end={hoursOfSelling > 0 ? netSales / hoursOfSelling : 0}
                        duration={1}
                        decimals={2}
                        separator=','
                      />{' '}
                      {CURRENCY_APP}
                    </span>
                  </div>
                </div>

                <div className='separator separator-dashed my-4'></div>

                {/* Sales Totals */}
                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Gross Sales:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp end={grossSales} duration={1} decimals={2} separator=',' />{' '}
                      {CURRENCY_APP}
                    </span>
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Cost of Goods:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp end={costOfGoods} duration={1} decimals={2} separator=',' />{' '}
                      {CURRENCY_APP}
                    </span>
                  </div>
                </div>

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Gross Deductions:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp end={grossDeductions} duration={1} decimals={2} separator=',' />{' '}
                      {CURRENCY_APP}
                    </span>
                  </div>
                </div>

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Net Sales:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp end={netSales} duration={1} decimals={2} separator=',' />{' '}
                      {CURRENCY_APP}
                    </span>
                  </div>
                </div>

                <div className='separator separator-dashed my-4'></div>

                {/* Payment Methods - Dynamically Rendered */}
                {paymentTotals.map((payment: any) => (
                  <div className='row mb-3' key={payment.payment_type_name}>
                    <div className='col-6'>
                      <span className='fw-bold fs-6'>
                        {formatPaymentTypeName(payment.payment_type_name)}:
                      </span>
                    </div>
                    <div className='col-6 text-end'>
                      <span className='fw-bold fs-6'>
                        <CountUp
                          end={safeNumber(payment.total_amount)}
                          duration={1}
                          decimals={2}
                          separator=','
                        />{' '}
                        {CURRENCY_APP}
                      </span>
                    </div>
                  </div>
                ))}

                <div className='separator separator-dashed my-4'></div>

                {/* Cash Float */}
                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Opening Float:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp end={openingBalance} duration={1} decimals={2} separator=',' />{' '}
                      {CURRENCY_APP}
                    </span>
                  </div>
                </div>

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Closing Float:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      {closingBalance !== null ? (
                        <>
                          <CountUp end={closingBalance} duration={1} decimals={2} separator=',' />{' '}
                          {CURRENCY_APP}
                        </>
                      ) : (
                        'Not Closed'
                      )}
                    </span>
                  </div>
                </div>

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Total Expenses:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp end={totalExpenses} duration={1} decimals={2} separator=',' />{' '}
                      {CURRENCY_APP}
                    </span>
                  </div>
                </div>

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Net Profit/Loss:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className={`fw-bold fs-6 ${net_profit_loss < 0 ? 'text-danger' : 'text-success'}`}>
                      <CountUp
                        end={net_profit_loss}
                        duration={1}
                        decimals={2}
                        separator=','
                      />{' '}
                      {CURRENCY_APP}
                    </span>
                  </div>
                </div>

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>Expected Cash:</span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className='fw-bold fs-6'>
                      <CountUp
                        end={expectedCashAtEOD}
                        duration={1}
                        decimals={2}
                        separator=','
                      /> {CURRENCY_APP}
                    </span>
                  </div>
                </div>

                {/* Fixed Cash Variance Section */}
                {closingBalance !== null && variance !== null && (
                  <div className='row mb-4'>
                    <div className='col-6'>
                      <span className='fw-bold fs-5'>

                        Cash Variance:
                      </span>
                    </div>
                    <div className='col-6 text-end'>
                      <span className='fw-bold fs-6'>
                        <CountUp
                          end={variance}
                          duration={1}
                          decimals={2}
                          separator=','
                          prefix={variance >= 0 ? '+' : ''}
                        /> {CURRENCY_APP}

                      </span>
                    </div>
                  </div>
                )}

                <div className='row mb-3'>
                  <div className='col-6'>
                    <span className='fw-bold fs-6'>
                      Float Status:
                    </span>
                  </div>
                  <div className='col-6 text-end'>
                    <span className={`badge ${closingBalance !== null ? 'badge-success' : 'badge-warning'
                      } fs-6 px-3 py-2`}>
                      <i className={`bi ${closingBalance !== null ? 'bi-lock-fill' : 'bi-unlock-fill'
                        } me-1`} style={{ color: 'black' }}></i>
                      {closingBalance !== null ? 'Closed' : 'Open'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className='modal-footer border-0'>
          <button type='button' className='btn btn-light-primary' onClick={props.handleClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
})